import React, { useState } from 'react';
import { ChevronRight, Receipt, Truck, RotateCcw, Camera } from 'lucide-react';
import { formatDate } from '../../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN   = () => sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
const H       = () => ({ Authorization: `Bearer ${TOKEN()}` });

const STATUT_META = {
  brouillon:    { label: 'Brouillon',    color: '#94a3b8',           bg: 'rgba(148,163,184,.15)' },
  envoye:       { label: 'Envoyé',       color: 'var(--admin-blue)', bg: 'var(--admin-blue-dim)' },
  signe:        { label: 'Signé',        color: 'var(--admin-teal)', bg: 'var(--admin-teal-dim)' },
  acompte_paye: { label: 'Acompte payé', color: 'var(--admin-gold)', bg: 'var(--admin-gold-dim)' },
  valide_admin: { label: 'Validé',       color: 'var(--admin-green)',bg: 'var(--admin-green-dim)' },
  solde_paye:   { label: 'Soldé',        color: 'var(--admin-green)',bg: 'var(--admin-green-dim)' },
  commande:     { label: 'Commande',     color: 'var(--admin-green)',bg: 'var(--admin-green-dim)' },
  termine:      { label: 'Terminé',      color: 'var(--admin-green)',bg: 'var(--admin-green-dim)' },
  annule:       { label: 'Annulé',       color: '#ef4444',           bg: 'rgba(239,68,68,.12)' },
};
const PAID_STATUTS = ['acompte_paye', 'solde_paye', 'commande', 'valide_admin', 'termine'];

function photoSrc(p) {
  const b = p.base64 || '';
  if (b) return b.startsWith('data:') ? b : `data:image/jpeg;base64,${b}`;
  return p.url || '';
}

// Carte location dépliable : n° devis + facture + statut + photos livraison/retour
export function LocationCard({ quote }) {
  const [open, setOpen]         = useState(false);
  const [photos, setPhotos]     = useState(null);
  const [loadingP, setLoadingP] = useState(false);

  const st       = STATUT_META[quote.statut] || STATUT_META.brouillon;
  const isPaid   = PAID_STATUTS.includes(quote.statut);
  const products = (quote.items || []).map(i => i.product_nom).filter(Boolean);
  const dateEvt  = quote.session?.date_evenement || quote.date_livraison;

  async function toggle() {
    const next = !open;
    setOpen(next);
    if (next && photos === null) {
      setLoadingP(true);
      try {
        const [l, r] = await Promise.all([
          fetch(`${BACKEND}/api/missions/${quote.id}/livraison/photos`, { headers: H() }).then(r => r.json()).catch(() => ({ photos: [] })),
          fetch(`${BACKEND}/api/missions/${quote.id}/retour/photos`,    { headers: H() }).then(r => r.json()).catch(() => ({ photos: [] })),
        ]);
        setPhotos({ livraison: l.photos || [], retour: r.photos || [] });
      } catch { setPhotos({ livraison: [], retour: [] }); }
      setLoadingP(false);
    }
  }

  return (
    <div className="adm-card" data-testid={`location-card-${quote.id}`}
      style={{ padding: 0, marginBottom: 8, overflow: 'hidden' }}>
      {/* En-tête cliquable */}
      <button onClick={toggle} data-testid={`location-card-toggle-${quote.id}`}
        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
          padding: '13px 15px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--admin-gold)',
              fontFamily: 'var(--admin-font-display)', letterSpacing: '.04em' }}>{quote.numero}</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 7,
              color: st.color, background: st.bg }}>{st.label}</span>
            {isPaid && (
              <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 7,
                color: 'var(--admin-gold)', background: 'var(--admin-gold-dim)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Receipt size={11}/> Facture {quote.numero}
              </span>
            )}
          </div>
          <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)', marginBottom: 2,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {products.slice(0, 2).join(', ') || 'Location'}{products.length > 2 ? ` +${products.length - 2}` : ''}
          </p>
          <p style={{ fontSize: 12, color: 'var(--admin-muted)' }}>
            {dateEvt ? `Événement : ${formatDate(dateEvt)}` : `Créé le ${formatDate(quote.created_at)}`}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--admin-font-display)', fontSize: 15, fontWeight: 700, color: 'var(--admin-text)' }}>
            {parseFloat(quote.total || 0).toFixed(0)}€
          </span>
          <ChevronRight size={16} style={{ color: 'var(--admin-faint)',
            transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}/>
        </div>
      </button>

      {/* Détail déplié : photos livraison / retour */}
      {open && (
        <div style={{ borderTop: '1px solid var(--admin-border-soft)', padding: '12px 15px' }}>
          {loadingP ? (
            <p style={{ fontSize: 12, color: 'var(--admin-faint)' }}>Chargement du suivi...</p>
          ) : (
            <>
              <PhotoRow icon={<Truck size={12}/>}      title="Photos de livraison" items={photos?.livraison || []} />
              <PhotoRow icon={<RotateCcw size={12}/>}  title="Photos de retour"    items={photos?.retour || []} />
              {(!photos?.livraison?.length && !photos?.retour?.length) && (
                <p style={{ fontSize: 12, color: 'var(--admin-faint)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Camera size={13}/> Aucune photo de livraison ou de retour disponible.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PhotoRow({ icon, title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <p style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--admin-muted)', marginBottom: 6,
        display: 'flex', alignItems: 'center', gap: 5 }}>{icon} {title} ({items.length})</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {items.map((p, i) => {
          const src = photoSrc(p);
          if (!src) return null;
          return (
            <a key={p.id || i} href={src} target="_blank" rel="noreferrer"
              style={{ display: 'block', width: 64, height: 64, borderRadius: 8, overflow: 'hidden',
                border: '1px solid var(--admin-border-soft)' }}>
              <img src={src} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </a>
          );
        })}
      </div>
    </div>
  );
}
